import { memo, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { UnpublishProject } from './UnpublishProject';
import { ConfirmationDialog } from '~/components/dialogs/ConfirmationDialog/ConfirmationDialog.client';
import { SectionRow } from '~/components/dialogs/settings/SectionContainer';
import { Button } from '~/components/ui/Button';
import { removeNetlifyHosting } from '~/lib/.client/api/domains';
import { patchHostingProvider } from '~/lib/.client/api/projects';
import { MSG_DEPLOY } from '~/lib/constants';
import { chatStore } from '~/lib/stores/chat';
import { domainsStore } from '~/lib/stores/domains';
import { menuStore } from '~/lib/stores/menu';
import { projectStore } from '~/lib/stores/project';
import { invariant } from '~/utils/invariant';
import { withSpinner } from '~/utils/withSpinner';

export const AppPublishedThroughNetlifyHosting = memo(({ url, projectId }: { url: string; projectId: string }) => {
  const [confirmSwitchToBoltHosting, setConfirmSwitchToBoltHosting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSwitchToBoltHosting = useCallback(() => {
    setConfirmSwitchToBoltHosting(true);
  }, []);

  const handleCloseConfirmation = useCallback(() => {
    setConfirmSwitchToBoltHosting(false);
  }, []);

  const handleConfirmDomainAction = useCallback(async () => {
    if (!projectId) {
      toast.error('Project ID is required');
      return;
    }

    setIsLoading(true);

    await withSpinner({
      action: async () => {
        try {
          const slug = projectStore.getSlug();

          invariant(slug);

          await patchHostingProvider(slug, 'bolt');
          projectStore.integrations.set({ hosting: { data: { hostingProvider: 'bolt' } } });

          await removeNetlifyHosting(projectId);
          chatStore.events.dispatch('sendChatMessage', { message: MSG_DEPLOY, mode: 'build' });

          setConfirmSwitchToBoltHosting(false);
          menuStore.closeDialog();
          domainsStore.resetAll();
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Failed to switch Bolt hosting');
        }
      },
      onStateUpdate: setIsLoading,
    });
  }, [projectId]);

  return (
    <>
      <p className="text-bolt-ds-textSecondary">
        Your project is published through <b className="text-bolt-ds-textPrimary">Netlify hosting</b>. You can move to
        the new architecture by <strong className="text-bolt-ds-textPrimary">switching to Bolt hosting</strong> and
        republishing the project.
      </p>

      <Button className="w-min" size="md" variant="primary" onClick={handleSwitchToBoltHosting}>
        Switch to Bolt hosting
      </Button>

      <SectionRow
        layout="vertical"
        title="Hosting settings"
        innerClassName="flex flex-col gap-4"
        titleClassName="text-bolt-ds-textTertiary font-semibold mb-0"
        action={<UnpublishProject url={url} projectId={projectId} />}
      />

      <ConfirmationDialog
        titleText="Confirm switch to Bolt hosting"
        headerWrapperClassName="gap-6"
        maxWidth={512}
        confirmButtonNode="Confirm hosting switch"
        confirmButtonVariant="warning"
        confirmButtonType="tint"
        open={confirmSwitchToBoltHosting}
        isConfirming={isLoading}
        onClose={handleCloseConfirmation}
        handleConfirm={handleConfirmDomainAction}
      >
        <p>
          You are about to remove the legacy Netlify hosting. You will be able to use the new architecture by following
          Bolt’s instructions. This action cannot be undone.
        </p>
      </ConfirmationDialog>
    </>
  );
});

AppPublishedThroughNetlifyHosting.displayName = 'AppPublishedThroughNetlifyHosting';
